import React, { useContext, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DataContext } from '../../contexto/ContextoDatos';
import Layout from '../../componentes/Plantilla/Plantilla';
import styles from './MagnoCategoria.module.css';
import Breadcrumbs from '../../componentes/UI/Breadcrumbs';
import { useScrollAnimation } from '../../ganchos/usarAnimacionScroll';
import marcasData from '../../datos/marcas.json';

import { CheckCircle, ShieldCheck, Zap, Factory } from 'lucide-react';

const MACRO_INFO = {
  "aspiracion-y-extraccion": {
    titulo: "Aspiración y Extracción",
    desc: "Sistemas avanzados de recolección de polvo, humos y sólidos para mantener su planta segura y cumplir con normativas industriales.",
    imagen: "/imagenes/productos/aspiradoras-industriales-1.webp",
    subcategorias: ["aspiradoras-industriales", "aspiradoras-comerciales", "colectores-y-ciclones-de-polvo"],
    descripcionesSub: {
      "aspiradoras-industriales": "Sistemas robustos diseñados para recolección de polvos pesados, líquidos y virutas en entornos hostiles y operaciones continuas.",
      "aspiradoras-comerciales": "Equipos versátiles y silenciosos de alto rendimiento para la limpieza diaria en oficinas, hoteles y espacios cerrados.",
      "colectores-y-ciclones-de-polvo": "Sistemas centralizados para capturar partículas en suspensión directamente de la fuente de producción, protegiendo el aire."
    },
    logros: [
      { titulo: "Cumplimiento Normativo", desc: "Asegura el cumplimiento con estándares OSHA y ATEX." },
      { titulo: "Salud Ocupacional", desc: "Protección integral de la salud respiratoria del personal." },
      { titulo: "Seguridad Industrial", desc: "Prevención activa de explosiones por polvos combustibles." },
      { titulo: "Retorno de Inversión", desc: "Recuperación de materia prima valiosa y reducción de mermas." }
    ],
    marcasAsociadas: ["Delfin", "Metrovac"]
  },
  "tratamiento-de-pisos": {
    titulo: "Tratamiento de Pisos",
    desc: "Equipos automatizados para el fregado, pulido y limpieza profunda de todo tipo de superficies, garantizando un entorno impecable.",
    imagen: "/imagenes/productos/fregadoras-de-piso-1.webp",
    subcategorias: ["fregadoras-de-piso", "pulidoras-de-piso", "limpiadores-a-vapor-seco"],
    descripcionesSub: {
      "fregadoras-de-piso": "Equipos de fregado y secado simultáneo para mantener pisos industriales y comerciales limpios, secos y seguros al instante.",
      "pulidoras-de-piso": "Máquinas rotativas de alta revolución para pulir, cristalizar y restaurar el brillo original de superficies duras.",
      "limpiadores-a-vapor-seco": "Desinfección y desengrase profundo sin químicos agresivos mediante la inyección de vapor a altísimas temperaturas."
    },
    logros: [
      { titulo: "Eficiencia Operativa", desc: "Reducción de hasta un 70% en tiempo de limpieza de grandes áreas." },
      { titulo: "Prevención de Accidentes", desc: "Superficies transitables inmediatamente, eliminando riesgos de caídas." },
      { titulo: "Desinfección Total", desc: "Eliminación del 99.9% de bacterias sin uso intensivo de químicos." },
      { titulo: "Protección de Activos", desc: "Conservación a largo plazo de la vida útil de los revestimientos epóxicos y duros." }
    ],
    marcasAsociadas: ["Powr-Flite"]
  },
  "alta-presion": {
    titulo: "Alta Presión",
    desc: "Hidrolavadoras de alta y súper alta presión para la remoción de incrustaciones severas, limpieza de tuberías y mantenimiento pesado.",
    imagen: "/imagenes/productos/hidrolavadoras-industriales-1.webp",
    subcategorias: ["hidrolavadoras-industriales", "hidrolavadoras-de-super-alta-presion", "hidrolavadoras-equipos-de-lavado-super-alta-presion", "limpieza-de-tuberias", "hidrolavadoras-accesorios"],
    descripcionesSub: {
      "hidrolavadoras-industriales": "Equipos de agua fría y caliente para el desengrase rápido y limpieza a fondo de maquinaria e instalaciones.",
      "hidrolavadoras-de-super-alta-presion": "Potencia extrema hidráulica para remover pintura, óxido e incrustaciones severas en astilleros y construcción.",
      "hidrolavadoras-equipos-de-lavado-super-alta-presion": "Sistemas especializados y ultra robustos diseñados para trabajos de grado industrial excepcionalmente pesados.",
      "limpieza-de-tuberias": "Tecnología de boquillas de retroceso para el desazolve y destape de ductos, drenajes y tuberías industriales.",
      "hidrolavadoras-accesorios": "Lanzas telescópicas, boquillas rotativas y componentes de grado aeronáutico para maximizar el rendimiento."
    },
    logros: [
      { titulo: "Remoción Extrema", desc: "Eliminación efectiva de incrustaciones severas sin dañar los sustratos." },
      { titulo: "Continuidad Operativa", desc: "Desazolve rápido de líneas de producción para evitar paros costosos." },
      { titulo: "Preparación de Superficies", desc: "Limpieza profunda para la óptima adherencia de nuevos recubrimientos." },
      { titulo: "Eco-Friendly", desc: "Limpieza mecánica profunda que elimina el uso intensivo de solventes tóxicos." }
    ],
    marcasAsociadas: ["Annovi Reverberi"]
  },
  "lavanderia": {
    titulo: "Lavandería Industrial",
    desc: "Equipos completos para el lavado, secado y planchado institucional con barrera sanitaria y máxima eficiencia energética.",
    imagen: "/imagenes/productos/lavanderia-industrial-lavadoras-rigidas-1.webp",
    subcategorias: ["lavanderia-comercial", "lavanderia-de-pequena-capacidad", "lavanderia-industrial-lavadoras-rigidas", "lavanderia-industrial-lavadoras-flotantes", "lavanderia-industrial-barrera-sanitaria", "lavanderia-industrial-secadoras", "lavanderia-industrial-planchadoras-de-rodillos", "lavanderia-industrial-calandras-con-plegadora", "lavanderia-industrial-acabados", "lavanderia-industrial-domus-compact-line", "lavanderia-industrial-ecotank-recuperacion-de-agua"],
    descripcionesSub: {
      "lavanderia-comercial": "Máquinas robustas de capacidad intermedia ideales para hoteles boutique, gimnasios y restaurantes.",
      "lavanderia-de-pequena-capacidad": "Equipos semi-industriales compactos, perfectos para pequeñas residencias, estéticas y spas.",
      "lavanderia-industrial-lavadoras-rigidas": "Lavadoras extractoras de anclaje rígido al piso, garantizando máxima durabilidad para cargas muy pesadas.",
      "lavanderia-industrial-lavadoras-flotantes": "Sistemas de alta extracción amortiguados sin necesidad de anclaje, ideales para instalarse en pisos superiores.",
      "lavanderia-industrial-barrera-sanitaria": "Diseño de doble puerta para prevención estricta de contaminación cruzada, obligatorio para hospitales y laboratorios.",
      "lavanderia-industrial-secadoras": "Sistemas eficientes de secado rápido con controles de humedad que cuidan la integridad textil de los blancos.",
      "lavanderia-industrial-planchadoras-de-rodillos": "Equipos de planchado continuo para grandes volúmenes de ropa plana, entregando un acabado impecable al instante.",
      "lavanderia-industrial-calandras-con-plegadora": "Automatización total de alto rendimiento que realiza el planchado y plegado longitudinal en un solo paso.",
      "lavanderia-industrial-acabados": "Mesas de desmanchado, prensas neumáticas y maniquíes profesionales para dejar las prendas de forma impecable.",
      "lavanderia-industrial-domus-compact-line": "La potencia de un sistema industrial de lavandería comprimida estratégicamente en el mínimo espacio posible.",
      "lavanderia-industrial-ecotank-recuperacion-de-agua": "Sistemas inteligentes de recuperación de agua para ahorrar hasta un 70% del vital líquido por cada ciclo de lavado."
    },
    logros: [
      { titulo: "Ahorro de Recursos", desc: "Ahorro dramático de hasta 70% en consumo de agua y energía eléctrica con ECOTANK." },
      { titulo: "Seguridad Sanitaria", desc: "Prevención absoluta de Infecciones Nosocomiales mediante diseño de Barreras Sanitarias." },
      { titulo: "Rentabilidad Automatizada", desc: "Reducción drástica de costos operativos y tiempos de ciclo mediante procesos controlados." },
      { titulo: "Cuidado Textil", desc: "Extensión de la vida útil de los blancos hospitalarios y hoteleros mediante programación inteligente." }
    ],
    marcasAsociadas: ["Domus"]
  }
};

const MagnoCategoria = () => {
  const { macroSlug } = useParams();
  const { categories, products } = useContext(DataContext);


  const [refHero, heroVisible] = useScrollAnimation();
  const [refGrid, gridVisible] = useScrollAnimation();
  const [refLogros, logrosVisible] = useScrollAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [macroSlug]);

  const info = MACRO_INFO[macroSlug];

  // Si no existe la macro categoría, redirigir al índice de productos
  if (!info) {
    return <Navigate to="/productos" replace />;
  }

  // Filtrar las categorías completas a partir de los slugs
  const macroCategorias = info.subcategorias.map(slug => {
    const catData = categories.find(c => c.slug === slug);
    const catProducts = products.filter(p => p.category === catData?.name);
    
    const firstProduct = catProducts.length > 0 ? catProducts[0] : null;
    
    // Obtenemos la imagen del primer producto usando su slug
    const firstProductImage = firstProduct 
      ? `/imagenes/productos/${firstProduct.slug}-1.webp` 
      : null;

    return {
      ...catData,
      count: catProducts.length,
      desc: info.descripcionesSub[slug] || "Sistemas especializados industriales.",
      image: firstProductImage
    };
  }).filter(c => c && c.name);

  // Ya no filtramos por marcasAsociadas, usamos todas las marcas disponibles como pidió el usuario
  const marcasAsociadas = marcasData;

  const IconosLogros = [CheckCircle, ShieldCheck, Zap, Factory];

  return (
    <Layout>
      <div className={styles.paginaMagno}>
        {/* Hero Banner (LimpiezaTuberia Style) */}
        <section 
          ref={refHero} 
          className={`${styles.hero} ${heroVisible ? styles.visible : ''}`}
          style={{ backgroundImage: `url(${info.imagen})` }}
        >
          <div className={styles.heroOverlay} />
          <div className={`container ${styles.heroContenedorInterior}`}>
            <div className={styles.heroContenido}>
              <Breadcrumbs 
                paths={[
                  { label: 'Inicio', to: '/' },
                  { label: 'Productos', to: '/productos' },
                  { label: info.titulo }
                ]}
              />
              <h1 className={styles.tituloPrincipal}>
                {info.titulo}
              </h1>
              <div className={styles.heroLine} />
              <p className={styles.descripcionPrincipal}>
                {info.desc}
              </p>
              <div className={styles.heroCtas}>
                <Link to="/productos" className="btn btn-primary">Ver todos los productos</Link>
              </div>
            </div>

            {/* Marcas Flotantes */}
            {marcasAsociadas.length > 0 && (
              <div className={styles.marcasFlotantesWrapper}>
                <h3 className={styles.marcasFlotantesTitle}>Marcas Asociadas</h3>
                <div className={styles.marcasFlotantesContainer}>
                  {marcasAsociadas.map((marca, idx) => (
                    <div key={marca.id} className={`${styles.marcaFlotante} ${styles[`flotantePos${idx % 8}`]}`}>
                      <img src={`/imagenes/marcas/${marca.archivo}`} alt={marca.nombre} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

      {/* Lista de Categorías */}
      <section ref={refGrid} className={`${styles.seccionCategorias} ${gridVisible ? styles.visible : ''}`}>
        <div className="container">
          <div className={styles.encabezadoSeccion}>
            <h2 className={styles.tituloSeccion}>Sistemas <span className={styles.acento}>Disponibles</span></h2>
            <div className={styles.lineaAcento} />
          </div>
          <div className={styles.gridCategorias}>
            {macroCategorias.map(cat => (
              <Link 
                key={cat.slug} 
                to={`/productos?categorias=${cat.slug}`}
                className={styles.tarjetaCategoria}
              >
                <div className={styles.imagenCategoriaWrapper}>
                  {cat.image ? (
                    <img 
                      src={cat.image} 
                      alt={cat.name} 
                      className={styles.imagenCategoria}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="fallback-logo">SEIMA</div>';
                      }}
                    />
                  ) : (
                    <div className="fallback-logo">SEIMA</div>
                  )}
                  <div className={styles.imagenOverlay}>
                    <span className={styles.btnVerMas}>Ver Productos</span>
                  </div>
                </div>
                <div className={styles.infoCategoria}>
                  <h3 className={styles.tituloCategoria}>{cat.name}</h3>
                  <p className={styles.descCategoria}>{cat.desc}</p>
                  <div className={styles.footerCategoria}>
                    <span className={styles.conteoEquipos}>{cat.count} EQUIPOS</span>
                    <ArrowRight size={18} className={styles.flechaCat} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className={styles.catalogoBtnWrapper}>
            <Link to="/productos" className={styles.btnCatalogoCompleto}>
              Ver Catálogo Completo
            </Link>
          </div>
        </div>
      </section>

      {/* Logros */}
      <section ref={refLogros} className={`${styles.seccionLogros} ${logrosVisible ? styles.visible : ''}`}>
        <div className="container">
          <div className={styles.encabezadoSeccion}>
            <h2 className={styles.tituloSeccion}>¿Qué puedes <span className={styles.acento}>lograr?</span></h2>
            <p className={styles.subtituloSeccion}>El impacto real de nuestros sistemas en su operación diaria.</p>
          </div>
          <div className={styles.gridLogros}>
            {info.logros.map((logro, idx) => {
              const Icono = IconosLogros[idx % IconosLogros.length];
              return (
                <div key={idx} className={styles.tarjetaLogro}>
                  <div className={styles.iconoLogroWrapper}>
                    <Icono className={styles.iconoLogro} size={32} />
                  </div>
                  <h4 className={styles.tituloLogro}>{logro.titulo}</h4>
                  <p className={styles.descLogro}>{logro.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      </div>
    </Layout>
  );
};

export default MagnoCategoria;
